import React, { useEffect, useMemo } from "react";

import { InlineFilter } from "components/Filters/InlineFilter";
import { useActionHook } from "hooks";
import { useAccountSelector, useReportsActions } from "store";
import type { Filters, ReportFilters } from "store/types";

import { GlobalFilterBar } from "./GlobalFilterBar";

interface Props {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

// Investor is surfaced as its own chip (GlobalFilterBar), so it is excluded from the InlineFilter list.
const INVESTOR_KEY = "Investor";

// Stable reference so InlineFilter's `state` effect doesn't refire on every render when no report filters are set.
const EMPTY_REPORT_FILTERS: Record<string, string[]> = {};

// Build the get-filters payload from the current selection (investor chip + dynamic report filters).
const toReportFilters = (filters: Filters): ReportFilters => {
  const result: ReportFilters = { ...(filters.reportFilters ?? {}) };
  if (filters.investors.length > 0) {
    result[INVESTOR_KEY] = filters.investors;
  }
  return result;
};

// Same InlineFilter as the reports QueryManager, but the investor filter stays a chip (GlobalFilterBar)
// injected via `additionalFilters` instead of being one of the InlineFilter entries.
export const ReportFilterBar: React.FC<Props> = ({ filters, onFiltersChange }) => {
  const { disable_apply_filters } = useAccountSelector() || {};
  const { getFilters } = useReportsActions();
  const { data: filtersList, action: fetchFilters } = useActionHook(getFilters);

  // Load the available filter values on mount; the options cascade off the active selection.
  useEffect(() => {
    fetchFilters(toReportFilters(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inlineFiltersList = useMemo(() => {
    const { [INVESTOR_KEY]: _investor, ...rest } = filtersList ?? {};
    return rest;
  }, [filtersList]);

  const inlineState = useMemo(() => filters.reportFilters ?? EMPTY_REPORT_FILTERS, [filters.reportFilters]);

  const handleChangeFilter = (next: Record<string, Array<number | string | boolean>>) => {
    const reportFilters: Record<string, string[]> = Object.fromEntries(
      Object.entries(next).map(([key, values]) => [key, values.map(String)])
    );
    onFiltersChange({ ...filters, reportFilters });
  };

  const refetchFilters = (next?: Record<string, Array<number | string | boolean>>) => {
    if (disable_apply_filters) {
      fetchFilters(toReportFilters(filters));
      return;
    }
    fetchFilters({
      ...(next ?? {}),
      ...(filters.investors.length > 0 ? { [INVESTOR_KEY]: filters.investors } : {})
    });
  };

  return (
    <InlineFilter
      state={inlineState}
      filtersList={inlineFiltersList}
      applyMode={!disable_apply_filters}
      handleChangeFilter={handleChangeFilter}
      refetchFilters={refetchFilters}
      additionalFilters={<GlobalFilterBar filters={filters} onFiltersChange={onFiltersChange} />}
      onClearAdditionalFilters={() => onFiltersChange({ ...filters, investors: [], reportFilters: {} })}
    />
  );
};
